import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * AdCarousel — Instagram-style photo carousel for a sponsored feed post. Each
 * photo sits in a fixed square frame and is letterboxed (object-contain) so
 * posters and logos are never cropped. Swipe on touch, tap the dots, or use the
 * desktop arrows; a counter badge tracks the position. No autoplay, matching
 * the feed.
 */
export default function AdCarousel({ images, title, href, onSlideClick }) {
    const trackRef = useRef(null);
    const [index, setIndex] = useState(0);
    const count = images?.length || 0;

    const scrollTo = useCallback(
        (i) => {
            const track = trackRef.current;
            if (!track) return;
            const next = Math.max(0, Math.min(count - 1, i));
            track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
        },
        [count]
    );

    const handleScroll = useCallback(() => {
        const track = trackRef.current;
        if (!track || !track.clientWidth) return;
        const i = Math.round(track.scrollLeft / track.clientWidth);
        setIndex((prev) => (prev === i ? prev : i));
    }, []);

    // Keep the active slide aligned on resize/orientation change.
    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const onResize = () => {
            track.scrollLeft = index * track.clientWidth;
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [index]);

    if (!count) return null;

    return (
        <div className="relative bg-white">
            <div
                ref={trackRef}
                onScroll={handleScroll}
                className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory"
            >
                {images.map((src, i) => (
                    <a
                        key={src}
                        href={href}
                        onClick={() => onSlideClick?.()}
                        className="relative w-full flex-shrink-0 snap-center aspect-square bg-white no-underline"
                        aria-label={`${title} photo ${i + 1} of ${count}`}
                    >
                        <img
                            src={src}
                            alt={i === 0 ? title : ''}
                            className="absolute inset-0 w-full h-full object-contain"
                            loading={i === 0 ? 'eager' : 'lazy'}
                            draggable={false}
                        />
                    </a>
                ))}
            </div>

            {count > 1 && (
                <>
                    <span className="absolute top-3 right-3 z-10 px-2 py-0.5 rounded-full bg-black/55 text-white text-xs font-semibold tabular-nums select-none">
                        {index + 1}/{count}
                    </span>

                    <div className="absolute bottom-3 inset-x-0 z-10 flex justify-center">
                        <div className="flex items-center gap-1.5 rounded-full bg-black/30 backdrop-blur px-2.5 py-1.5">
                            {images.map((src, i) => (
                                <button
                                    key={src}
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        scrollTo(i);
                                    }}
                                    aria-label={`Go to photo ${i + 1}`}
                                    aria-current={i === index}
                                    className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 bg-white' : 'w-1.5 bg-white/55'}`}
                                />
                            ))}
                        </div>
                    </div>

                    {index > 0 && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollTo(index - 1);
                            }}
                            aria-label="Previous photo"
                            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 rounded-full bg-white/85 ring-1 ring-black/10 shadow hover:bg-white text-gray-900"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    )}

                    {index < count - 1 && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollTo(index + 1);
                            }}
                            aria-label="Next photo"
                            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 rounded-full bg-white/85 ring-1 ring-black/10 shadow hover:bg-white text-gray-900"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
